<script setup>
import { onBeforeMount, ref, toRefs, watch } from "vue";
import * as amplitude from "@amplitude/analytics-browser";
import ok from "@/assets/icons/ok.svg";
import redCross from "@/assets/icons/redCross.svg";
import goodExample from "@/assets/new-good-example.png";
import goodExampleHybrid from "@/assets/hybrid_qr.png";
import withTimeLimit from "../utils/asyncHelpers.js";

const props = defineProps({
  toggle: Function,
  visible: Boolean,
  exitURL: String,
  product: Object,
  brand: Object,
  photo: String,
  isHybrid: Boolean,
  isIos: Boolean,
  camerasCount: { type: Number, default: 0 },
});

const { visible, exitURL } = toRefs(props);
const counter = ref(0);
const timerId = ref(null);

async function close(isExit) {
  if (isExit) {
    await withTimeLimit(amplitude.track("Exit authentication", window.amplitudeCommonProperties).promise.then(() => amplitude.flush()));
    window.location.href = exitURL.value;
  } else {
    amplitude.track("Scan again", window.amplitudeCommonProperties);
    props.toggle(false);
  }
}

watch(visible, () => {
  if (visible.value) {
    timerId.value = setInterval(() => {
      counter.value += 1;
      if (counter.value === 3) {
        clearInterval(timerId.value);
        timerId.value = null;
      }
    }, 1000);
  } else {
    if (timerId.value) {
      clearInterval(timerId.value);
      timerId.value = null;
    }
    counter.value = 0;
  }
});
onBeforeMount(() => {
  if (timerId.value) {
    clearInterval(timerId.value);
    timerId.value = null;
  }
});

function handleClose() {
  if (counter.value < 3) {
    return;
  }
  close(false);
}
</script>

<template>
<div v-if="visible" class="text_all fixed top-[42px] right-0 bottom-5 left-0 z-[9998] overflow-y-auto bg-white px-3 pt-5">
  <div class="mb-11 flex flex-col text-center text-black">
    <h1 class="mb-1.5 text-2xl font-bold">
      {{ $t("unreadable.title") }}
    </h1>
    <div class="text-xl">
      {{ $t("unreadable.description") }}
    </div>
  </div>

  <div class="grid grid-cols-2 gap-2">
    <div class="flex flex-col items-center gap-3 px-4">
      <img :src="redCross" class="h-6 w-6" />
      <div class="m-auto flex max-h-[188px] w-full justify-center rounded bg-black drop-shadow-custom">
        <img :src="photo" class="h-full rounded-none object-cover" />
      </div>
    </div>
    <div class="flex flex-col items-center gap-3">
      <img :src="ok" class="h-6 w-6" />
      <div class="relative flex h-full max-h-[188px] w-auto items-center justify-center rounded">
        <span class="absolute top-1.5 right-0 left-0 z-50 mx-auto w-fit bg-white px-2.5 text-center text-xl font-bold">
          {{ $t("unreadable.example") }}
        </span>
        <img :src="isHybrid ? goodExampleHybrid : goodExample" class="h-full rounded object-contain drop-shadow-custom" />
      </div>
    </div>
  </div>

  <div class="mt-3 flex flex-col items-center justify-center gap-2.5 py-6 text-center">
    <button
      class="inline-flex w-auto min-w-28 rounded-sm bg-[var(--primary)] p-2.5 text-lg leading-none font-bold text-[var(--text-button)]"
      :class="{ '!bg-[#d8d8d8]': counter < 3 }"
      :disabled="counter < 3"
      @click="handleClose"
    >
      {{ $t("unreadable.again") }} {{ counter < 3 ? 3 - counter : "" }}
    </button>

    <button v-if="isIos || camerasCount > 1" class="text-center text-base underline" @click="$emit('select')">
      {{ isIos ? $t("select_phone_model") : $t("change_camera") }}
    </button>
  </div>
</div>
</template>
